/**
 * PII scrubber for Sentry event payloads.
 *
 * NCUA examiner readiness: scrub PII before transmission per credit union
 * data handling policy. OLA is member-facing and handles SSN, DOB, financial
 * data, account numbers, contact information. None of those raw values may
 * leave our infrastructure. The scrubber is FAIL-CLOSED: we'd rather over-
 * redact than ship raw member data.
 *
 * Approach (two-layer defense):
 *   1. Field-name based: walk the event payload recursively. For every
 *      object key, normalize the key (lowercase, strip non-alphanumerics)
 *      and check it against the deny-list patterns. On match, replace the
 *      value with `[REDACTED]`. The field name itself is preserved so error
 *      context is still useful for debugging shape, just not values.
 *   2. Value-pattern based (for string fields the SDK populates directly,
 *      e.g. `event.message`, `exception.value`, `breadcrumb.message`,
 *      `request.url`): apply regex-based scrubbing for well-known PII
 *      shapes (SSN, phone, email, card numbers) and parse URL query strings
 *      so any deny-list-named query param value is redacted.
 *
 * Depth-cap policy: deeper than MAX_DEPTH we FAIL CLOSED, returning REDACTED
 * for the subtree. Unlikely to be hit in practice, but the alternative
 * (pass-through) is a silent leak path indefensible to an examiner.
 *
 * New field names can be added to PII_FIELD_PATTERNS below.
 */

export const REDACTED = '[REDACTED]';

/**
 * Field-name patterns that identify PII. Each entry is a substring tested
 * against the normalized key (lowercased, alphanumerics only). A match on
 * any pattern redacts the value.
 *
 * Examples of what each pattern catches (after normalization):
 *   "ssn"            -> ssn, socialSecurityNumber (contains "ssn"? no — see
 *                       "socialsecurity"), social_security_number
 *   "socialsecurity" -> socialSecurityNumber, social_security_number
 *   "dob"            -> dob, DOB, date_of_birth (no — see "dateofbirth"),
 *                       birthDate (no — see "birth")
 *   "dateofbirth"    -> date_of_birth, dateOfBirth
 *   "birth"          -> birthDate, dateOfBirth, birthday, placeOfBirth
 *   "accountnumber"  -> account_number, accountNumber, acctNumber (no — see
 *                       below)
 *   "account"        -> account, accountId, accountNumber, acctNumber (no)
 *   "acct"           -> acctNumber, acctId
 *   "routingnumber"  -> routing_number, routingNumber, ach_routing
 *   "routing"        -> routingNumber, routingId
 *   "tin"            -> taxpayer identification number, ein/tin fields
 *   "ein"            -> employer identification number
 *   "email"          -> email, emailAddress, contactEmail
 *   "phone"          -> phone, phoneNumber, contactPhone, mobilePhone
 *   "address"        -> address, streetAddress, addressLine1, mailingAddress
 *   "street"         -> street, streetAddress, streetName
 *   "zip"            -> zip, zipCode, postalCode (no — see "postal")
 *   "postal"         -> postalCode, postal_code
 *   "firstname"      -> firstName, first_name
 *   "lastname"       -> lastName, last_name
 *   "middlename"     -> middleName
 *   "fullname"       -> fullName, full_name
 *   "maidenname"     -> maidenName
 *   "name"           -> name (BS-2167: catch-all). Note over-redaction
 *                       trade-off — `userName`, `routeName`, `fileName`,
 *                       `loanName` etc. will all get redacted. That's the
 *                       compliance-preferred direction; debugging context
 *                       you lose here is "this was a name-shaped thing".
 *                       Non-PII identifiers should use suffix `Id` not `Name`.
 *   "dl" / "license" -> driverLicense, driversLicense, dlNumber
 *   "passport"       -> passportNumber
 *   "income"         -> grossIncome, annualIncome, monthlyIncome
 *   "salary"         -> salary, annualSalary
 *   "balance"        -> balance, currentBalance, availableBalance
 *   "memberid"       -> memberId, member_id
 *   "membernumber"   -> memberNumber, member_number
 *   "password"       -> password, currentPassword
 *   "secret"         -> apiSecret, clientSecret, secretQuestion
 *   "token"          -> authToken, accessToken, refreshToken
 *   "credential"     -> credentials, credentialId
 *   "authorization"  -> authorization (HTTP header)
 *   "cookie"         -> cookie, setCookie (HTTP header)
 *   "creditcard"     -> creditCard, creditCardNumber
 *   "cardnumber"     -> cardNumber, card_number
 *   "cvv"            -> cvv, cvc, securityCode (no — see "securitycode")
 *   "cvc"            -> cvc
 *   "securitycode"   -> securityCode, card_security_code
 *   "pin"            -> pin, pinNumber, atmPin
 *   "ip" / "ipaddress" -> ip_address, ipAddress, clientIp (BS-2167)
 *   "url"            -> url, requestUrl, referrerUrl (BS-2167). URLs are
 *                       redacted entirely because they may carry query-
 *                       string PII; the string-level scrubber handles URL
 *                       strings stored under other keys.
 *   "query"          -> query, queryString, urlQuery (BS-2167)
 */
const PII_FIELD_PATTERNS: readonly string[] = [
  // Government IDs
  'ssn',
  'socialsecurity',
  'tin',
  'ein',
  'taxid',
  'license',
  'driverslicense',
  'driverlicense',
  'dlnumber',
  'passport',

  // Dates of birth
  'dob',
  'dateofbirth',
  'birth',
  'birthday',

  // Names (member identity)
  // BS-2167: `name` added as a substring catch-all. Accepts over-redaction
  // of fields like `userName`, `routeName`, `fileName`. Non-PII identifiers
  // should use suffix `Id` not `Name`.
  'name',
  'firstname',
  'lastname',
  'middlename',
  'fullname',
  'maidenname',
  'givenname',
  'familyname',
  'surname',

  // Contact
  'email',
  'phone',
  'mobile',
  'address',
  'street',
  'zip',
  'postal',
  'city',

  // Network identifiers (BS-2167)
  'ip',
  'ipaddress',
  'url',
  'query',

  // Financial accounts
  'account',
  'acct',
  'accountnumber',
  'routingnumber',
  'routing',
  'aba',
  'memberid',
  'membernumber',
  'membernum',

  // Cards
  'creditcard',
  'cardnumber',
  'cvv',
  'cvc',
  'securitycode',
  'pin',
  'expirationdate',
  'expdate',

  // Money (raw values, not aggregates)
  'income',
  'salary',
  'wage',
  'balance',
  'paymentamount',

  // Auth / secrets
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'credential',
  'authorization',
  'cookie',
  'session',
] as const;

const MAX_DEPTH = 12;

/**
 * Value-pattern regexes (BS-2167). Used to scrub raw string fields the SDK
 * populates directly: `event.message`, `event.transaction`,
 * `event.logentry.formatted`, `exception.values[].value`,
 * `breadcrumb.message`, `request.url`. Field-name matching doesn't help on
 * these — they're scalar strings whose VALUE may contain PII.
 *
 * Conservative-by-default: each regex matches a well-known PII shape and
 * replaces with [REDACTED]. We accept false positives (e.g. a 9-digit
 * support ticket number redacted as if it were an SSN) over false negatives.
 */

// SSN: 123-45-6789, 123 45 6789, 123456789
const SSN_REGEX = /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g;

// Email: simple RFC-5322 subset, sufficient for log scrubbing.
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

// US phone: (555) 123-4567, 555-123-4567, 555.123.4567, +1 555 123 4567,
// 5551234567. Matches on word boundaries so it doesn't grab the middle of
// account numbers — but the credit-card regex below catches longer digit
// runs.
const PHONE_REGEX = /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;

// Credit-card / long account-number-ish: 13-19 contiguous digits, optionally
// separated by spaces or dashes in groups. Run before phone so card numbers
// don't get partially matched.
const CARD_OR_ACCOUNT_REGEX = /\b(?:\d[ -]?){13,19}\d?\b/g;

/**
 * Scrub a free-form string for PII value patterns. Order matters: longer
 * shapes (cards) before shorter ones (phone) so the long-digit-run regex
 * doesn't get pre-empted.
 */
export function scrubStringValue(input: string): string {
  if (!input) return input;
  let out = input;
  out = out.replace(CARD_OR_ACCOUNT_REGEX, REDACTED);
  out = out.replace(SSN_REGEX, REDACTED);
  out = out.replace(EMAIL_REGEX, REDACTED);
  out = out.replace(PHONE_REGEX, REDACTED);
  return out;
}

/**
 * Scrub a URL string. Splits on `?`, walks `&`-separated query params, and
 * for any param whose key matches the deny-list, replaces the VALUE with
 * REDACTED. The path stays intact so route info survives for debugging.
 * After query-param scrubbing, the value side of every remaining param is
 * still run through `scrubStringValue` to catch PII-shaped values under
 * non-deny-listed keys (e.g. `?ref=member@example.com`).
 *
 * Non-URL strings (no `?`) pass through `scrubStringValue` only.
 */
export function scrubUrlString(input: string): string {
  if (!input || typeof input !== 'string') return input;

  const queryIndex = input.indexOf('?');
  if (queryIndex === -1) {
    return scrubStringValue(input);
  }

  const base = input.slice(0, queryIndex);
  const queryAndFragment = input.slice(queryIndex + 1);

  // Preserve URL fragment (`#...`) by splitting it off the query string.
  const fragmentIndex = queryAndFragment.indexOf('#');
  const rawQuery =
    fragmentIndex === -1 ? queryAndFragment : queryAndFragment.slice(0, fragmentIndex);
  const fragment = fragmentIndex === -1 ? '' : queryAndFragment.slice(fragmentIndex);

  const scrubbedParams = rawQuery
    .split('&')
    .map((pair) => {
      if (!pair) return pair;
      const eqIndex = pair.indexOf('=');
      if (eqIndex === -1) {
        // Bare key with no value — nothing to scrub.
        return pair;
      }
      const key = pair.slice(0, eqIndex);
      const value = pair.slice(eqIndex + 1);
      if (isPiiFieldName(decodeURIComponentSafe(key))) {
        return `${key}=${REDACTED}`;
      }
      // Even for non-deny-list keys, scrub PII-shaped values.
      const decodedValue = decodeURIComponentSafe(value);
      const scrubbed = scrubStringValue(decodedValue);
      if (scrubbed !== decodedValue) {
        return `${key}=${REDACTED}`;
      }
      return pair;
    })
    .join('&');

  return `${scrubStringValue(base)}?${scrubbedParams}${fragment}`;
}

/**
 * `decodeURIComponent` throws on malformed input. Return the raw string in
 * that case — better to scan the encoded form than crash the scrubber.
 */
function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Normalize a key for pattern matching: lowercase, strip everything that
 * isn't a letter or digit. This makes the deny-list match across naming
 * conventions: SSN, ssn, social_security_number, socialSecurityNumber,
 * social-security-number all become the same canonical form.
 */
export function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Returns true if the field name (after normalization) matches any PII
 * pattern. Exported for unit testing.
 */
export function isPiiFieldName(key: string): boolean {
  const normalized = normalizeKey(key);
  if (normalized.length === 0) return false;
  for (const pattern of PII_FIELD_PATTERNS) {
    if (normalized.includes(pattern)) {
      return true;
    }
  }
  return false;
}

/**
 * Recursively scrub PII from an arbitrary value. Returns the same shape with
 * PII-named fields replaced by REDACTED. Non-PII fields are walked but their
 * values left untouched (other than recursion).
 *
 * BS-2167: depth-cap is fail-closed. At MAX_DEPTH we return REDACTED for the
 * subtree rather than pass-through, so a deeply nested PII leak path is
 * impossible.
 */
export function scrubValue<T>(value: T, depth = 0): T {
  if (depth > MAX_DEPTH) return REDACTED as unknown as T;
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1)) as unknown as T;
  }

  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      if (isPiiFieldName(key)) {
        result[key] = REDACTED;
      } else {
        result[key] = scrubValue(source[key], depth + 1);
      }
    }
    return result as unknown as T;
  }

  // Primitives pass through structural scrubbing. Top-level Sentry string
  // fields (event.message, exception.value, etc.) get value-pattern
  // scrubbing in scrubEvent via scrubStringValue / scrubUrlString.
  return value;
}

/**
 * Scrub PII from a Sentry event payload. We loosely type the event because
 * the Sentry shape changes between major versions and we want to keep this
 * resilient. Any unknown extensions to the event shape are preserved
 * (recursive walk).
 *
 * BS-2167 expansions:
 *   - `event.message`, `event.transaction`, `event.logentry.formatted`,
 *     `exception.values[].value`, and `breadcrumb.message` get value-pattern
 *     scrubbing (SSN/phone/email/card regexes).
 *   - `request.url`-shaped strings get URL-aware query-string scrubbing.
 *   - Breadcrumb top-level scalars are walked, not just `breadcrumb.data`.
 */
export interface ScrubbableEvent {
  message?: unknown;
  transaction?: unknown;
  logentry?: {
    formatted?: unknown;
    [key: string]: unknown;
  };
  request?: unknown;
  contexts?: unknown;
  extra?: unknown;
  tags?: unknown;
  user?: unknown;
  breadcrumbs?: Array<{
    data?: unknown;
    message?: unknown;
    [key: string]: unknown;
  }>;
  exception?: {
    values?: Array<{
      value?: unknown;
      stacktrace?: {
        frames?: Array<{
          vars?: unknown;
          [key: string]: unknown;
        }>;
      };
      [key: string]: unknown;
    }>;
  };
  [key: string]: unknown;
}

/**
 * Scrub a value that we know is meant to hold a URL (e.g., `request.url`).
 * Non-string values pass through unchanged.
 */
function scrubUrlField(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return scrubUrlString(value);
}

/**
 * Scrub a value that we know is a free-form scalar string (e.g.,
 * `event.message`, `exception.value`). Non-strings pass through unchanged.
 */
function scrubScalarString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return scrubStringValue(value);
}

/**
 * Walk a request object and apply URL-aware scrubbing to its `url` field,
 * then structural scrub the rest. The structural pass would redact `url`
 * entirely (since `url` is in the deny-list); we beat it to the punch with
 * URL-aware scrubbing so the route/path survives for debugging while the
 * query string still gets sanitized.
 */
function scrubRequest(request: unknown): unknown {
  if (!request || typeof request !== 'object' || Array.isArray(request)) {
    return scrubValue(request);
  }
  const source = request as Record<string, unknown>;
  const scrubbedUrl = 'url' in source ? scrubUrlField(source.url) : undefined;
  // Structurally scrub the rest, then re-apply the URL-aware result.
  const structurallyScrubbed = scrubValue(source);
  if ('url' in source) {
    (structurallyScrubbed as Record<string, unknown>).url = scrubbedUrl;
  }
  return structurallyScrubbed;
}

/**
 * BS-2167 gap #3: Scrub a single breadcrumb. Walks `data` recursively (the
 * BS-2146 baseline) and additionally scrubs `message` for PII value
 * patterns. `message` may be a URL (Sentry's auto-instrumented navigation
 * breadcrumbs put paths here) so it routes through `scrubUrlString` which
 * sanitizes query-string params.
 *
 * Exported for the SDK `beforeBreadcrumb` hook in instrumentation-client /
 * sentry.server.config / sentry.edge.config.
 */
export function scrubBreadcrumb<T extends { data?: unknown; message?: unknown }>(crumb: T): T {
  if (!crumb || typeof crumb !== 'object') return crumb;
  const scrubbed: T = { ...crumb };
  if (crumb.data) {
    scrubbed.data = scrubValue(crumb.data);
  }
  if (typeof crumb.message === 'string') {
    (scrubbed as { message?: unknown }).message = scrubUrlString(crumb.message);
  }
  return scrubbed;
}

/**
 * Walk breadcrumbs and scrub both `data` (recursive structural) and
 * top-level scalar string fields (`message`) via value-pattern scrubbing.
 */
function scrubBreadcrumbs(
  breadcrumbs: NonNullable<ScrubbableEvent['breadcrumbs']>
): ScrubbableEvent['breadcrumbs'] {
  return breadcrumbs.map((crumb) => scrubBreadcrumb(crumb));
}

/**
 * Walk exception values and apply value-pattern scrubbing to each
 * `exception.value` (the human-readable error message) plus recursively
 * structural-scrub stacktrace frame `vars`.
 */
function scrubExceptionValues(
  values: NonNullable<NonNullable<ScrubbableEvent['exception']>['values']>
): NonNullable<ScrubbableEvent['exception']>['values'] {
  return values.map((exceptionValue) => {
    const scrubbed: typeof exceptionValue = { ...exceptionValue };
    if (typeof exceptionValue.value === 'string') {
      scrubbed.value = scrubStringValue(exceptionValue.value);
    }
    if (exceptionValue.stacktrace?.frames) {
      scrubbed.stacktrace = {
        ...exceptionValue.stacktrace,
        frames: exceptionValue.stacktrace.frames.map((frame) => ({
          ...frame,
          vars: frame.vars ? scrubValue(frame.vars) : frame.vars,
        })),
      };
    }
    return scrubbed;
  });
}

export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (!event || typeof event !== 'object') return event;

  const scrubbed: ScrubbableEvent = { ...event };

  // BS-2167 gap #1: top-level string fields that the SDK populates directly.
  if (typeof scrubbed.message === 'string') {
    scrubbed.message = scrubScalarString(scrubbed.message);
  }
  if (typeof scrubbed.transaction === 'string') {
    scrubbed.transaction = scrubScalarString(scrubbed.transaction);
  }
  if (scrubbed.logentry && typeof scrubbed.logentry === 'object') {
    const logentry = scrubbed.logentry as Record<string, unknown>;
    if (typeof logentry.formatted === 'string') {
      scrubbed.logentry = {
        ...logentry,
        formatted: scrubScalarString(logentry.formatted),
      };
    }
  }

  // BS-2167 gap #4: request.url gets URL-aware query-string scrubbing.
  if (scrubbed.request) {
    scrubbed.request = scrubRequest(scrubbed.request);
  }
  if (scrubbed.contexts) {
    scrubbed.contexts = scrubValue(scrubbed.contexts);
  }
  if (scrubbed.extra) {
    scrubbed.extra = scrubValue(scrubbed.extra);
  }
  if (scrubbed.tags) {
    scrubbed.tags = scrubValue(scrubbed.tags);
  }
  if (scrubbed.user) {
    scrubbed.user = scrubValue(scrubbed.user);
  }

  // BS-2167 gap #3: breadcrumb.message scrubbed via value-pattern.
  if (Array.isArray(scrubbed.breadcrumbs)) {
    scrubbed.breadcrumbs = scrubBreadcrumbs(scrubbed.breadcrumbs);
  }

  // BS-2167 gap #1 (cont): exception.values[].value scrubbed via value-pattern.
  if (scrubbed.exception?.values && Array.isArray(scrubbed.exception.values)) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: scrubExceptionValues(scrubbed.exception.values),
    };
  }

  return scrubbed as T;
}
